/**
 * The selection bar's popover chassis (`equipment-page-bulk-actions`, R2, R5, R6): frame 07's
 * *Rename as a set* — the disc and title, the body, and *Cancel* / the primary action — shared
 * by the three popovers so their chrome is one thing. *Change Product* and *Set 3D Model* are
 * readings on this drawn frame (frame 07 draws their buttons, not what opens; `PROVENANCE.md`).
 *
 * The disc is the Popover's default lavender, the *information* role (`semantic.status.roles`),
 * not frame 07's frost: frost now means selection on onboarding surfaces, and Rob ruled the disc
 * takes the role it matches, success or information (2026-10-28).
 *
 * A page composition over the design system's `Popover` and `Button`: no styled component of its
 * own at module scope, since the wizard's page suites mock `@repo/design-system` part by part.
 */
import React from 'react';
import { Button, Icon, Popover } from '@repo/design-system';
import type { IconName } from '@repo/design-system';

/** Frame 07's popover width (`Equipment Page.dc.html`, the rename popover: 440 px). */
export const BULK_POPOVER_WIDTH = 440;

export interface BulkPopoverFrameProps {
  id: string;
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  glyph: IconName;
  title: string;
  /** The primary action's words, e.g. *Rename 8*. */
  primaryLabel: string;
  onPrimary: () => void;
  /** Unavailable (`aria-disabled`, still focusable), with the reason as its name. */
  primaryUnavailable?: string | null;
  busy: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}

export const BulkPopoverFrame: React.FC<BulkPopoverFrameProps> = ({
  id,
  open,
  onClose,
  anchorRef,
  glyph,
  title,
  primaryLabel,
  onPrimary,
  primaryUnavailable,
  busy,
  initialFocusRef,
  children,
}) => (
  <Popover
    id={id}
    open={open}
    onClose={onClose}
    anchorRef={anchorRef}
    title={title}
    glyph={glyph}
    placement="top"
    width={BULK_POPOVER_WIDTH}
    initialFocusRef={initialFocusRef}
    actions={
      <>
        <Button variant="text" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          iconLeft={<Icon name="check" size="sm" />}
          loading={busy}
          aria-disabled={primaryUnavailable || busy ? true : undefined}
          aria-label={primaryUnavailable ? `${primaryLabel} — ${primaryUnavailable}` : undefined}
          onClick={() => {
            if (!primaryUnavailable && !busy) onPrimary();
          }}
        >
          {primaryLabel}
        </Button>
      </>
    }
  >
    {children}
  </Popover>
);

export default BulkPopoverFrame;
